import { sortArrayOfObjects } from 'relapp-helpers';

/**
 * Sorts records by name and type.
 * @param  {array} records
 * @return {array}
 */
function sortRecords(records) {
    return records
        .sort((first, second) => sortArrayOfObjects(first, second, 'name'))
        .sort((first, second) => sortArrayOfObjects(first, second, 'type'));
}

const state = {
    records: [],
    domain: null,
    updating: false
};

const getters = {
    records: state => state.records,
    updating: state => state.updating
};

const actions = {
    /**
     * Set the updating status.
     *
     * @param {object} commit
     * @param {boolean} status
     */
    setStatus({ commit }, status) {
        try {
            commit('setStatus', status);
        } catch (error) {
            console.error(error);
            console.trace();
        }
    },

    /**
     * Set the domain.
     *
     * @param {object} commit, state
     * @param {string} domain
     */
    setDomain({ commit, state }, domain) {
        try {
            if (state.domain != domain) {
                commit('setDomain', domain);
                commit('resetRecords', {
                    records: []
                });
            }
        } catch (error) {
            console.error(error);
            console.trace();
        }
    },

    /**
     * Add a record.
     *
     * @param {object} commit
     * @param {object} record
     */
    addItem({ commit }, record) {
        try {
            commit('addRecord', record);
        } catch (error) {
            console.error(error);
            console.trace();
        }
    },

    /**
     * Delete a record.
     *
     * @param {object} commit
     * @param {object} record
     */
    removeItem({ commit }, record) {
        try {
            commit('deleteRecord', record);
        } catch (error) {
            console.error(error);
            console.trace();
        }
    },

    /**
     * Update a record.
     *
     * @param {object} commit
     * @param {object} oldRecord, newRecord, recordIndex
     */
    updateItem({ commit }, { oldRecord, newRecord, recordIndex }) {
        try {
            commit('updateRecord', {
                oldRecord: oldRecord,
                newRecord: newRecord,
                recordIndex: recordIndex
            });
        } catch (error) {
            console.error(error);
            console.trace();
        }
    },

    /**
     * Add all records.
     *
     * @param {object} commit, state
     * @param {array} records
     */
    updateItems({ commit, state }, records) {
        let existingRecords = [];

        records.forEach((record, recordIndex) => {
            let existingItem = state.records.find(
                item => item.name == record.name && item.type == record.type && item.content == record.content
            );

            if (recordIndex && state.records[recordIndex]) {
                existingItem = state.records[recordIndex];
            }

            existingRecords.push(record);

            try {
                if (!existingItem) {
                    commit('addRecord', record);
                } else {
                    commit('updateRecord', {
                        oldRecord: {
                            name: existingItem.name,
                            type: existingItem.type,
                            content: existingItem.content,
                            priority: existingItem.priority,
                            port: existingItem.port,
                            weight: existingItem.weight,
                            ttl: existingItem.ttl
                        },
                        newRecord: record,
                        recordIndex: recordIndex
                    });
                }
            } catch (error) {
                console.error(error);
                console.trace();
            }
        });

        commit('resetRecords', {
            records: existingRecords
        });
    },

    /**
     * Reset the subscriptions.
     *
     * @param {object} commit
     */
    reset({ commit }) {
        commit('reset');
    }
};

const mutations = {
    /**
     * Modify the updating status.
     *
     * @param {object} state
     * @param {boolean} status
     */
    setStatus(state, status) {
        if (typeof status != 'boolean') {
            throw {
                message: 'Mutation | Set status: status isnt a boolean',
                record: status,
                type: typeof status
            };
        }

        state.updating = status;
    },

    /**
     * Set the domain.
     *
     * @param {object} state
     * @param {string} domain
     */
    setDomain(state, domain) {
        if (domain != state.domain) {
            state.domain = domain;
        }
    },

    /**
     * Add record to the records state.
     *
     * @param {object} state
     * @param {object} record
     */
    addRecord(state, record) {
        if (typeof record != 'object' || !record.name) {
            throw {
                message: 'Mutation | Add record: record isnt a dns object',
                record: record,
                type: typeof record
            };
        }

        record.domain = state.domain;

        state.records.push({
            ...record
        });
        sortRecords(state.records);
    },

    /**
     * Replace the current record object with a new record.
     *
     * @param {object} state
     * @param {object} oldRecord, newRecord, recordIndex
     */
    updateRecord(state, { oldRecord, newRecord, recordIndex }) {
        if (typeof oldRecord != 'object' || !oldRecord.name) {
            throw {
                message: 'Mutation | Update record: old record isnt a dns object',
                record: oldRecord,
                type: typeof oldRecord
            };
        }

        if (typeof newRecord != 'object' || !newRecord.name) {
            throw {
                message: 'Mutation | Update record: new record isnt a dns object',
                record: newRecord,
                type: typeof newRecord
            };
        }

        let existingItem = state.records.find(
            item => item.name == oldRecord.name && item.type == oldRecord.type && item.content == oldRecord.content
        );

        if (recordIndex && state.records[recordIndex]) {
            existingItem = state.records[recordIndex];
        }

        if (!existingItem) {
            throw {
                message: 'Mutation | Update record: record not found',
                record: oldRecord,
                type: typeof oldRecord
            };
        }

        if (JSON.stringify(newRecord) != JSON.stringify(existingItem)) {
            existingItem.name = newRecord.name;
            existingItem.content = newRecord.content;
            existingItem.priority = newRecord.priority;
            existingItem.port = newRecord.port;
            existingItem.weight = newRecord.weight;
            existingItem.ttl = newRecord.ttl;
        }
    },

    /**
     * Delete a record.
     *
     * @param {object} state
     * @param {object} record
     */
    deleteRecord(state, record) {
        if (typeof record != 'object' || !record.name) {
            throw {
                message: 'Mutation | Delete record: record isnt a dns object',
                record: record,
                type: typeof record
            };
        }

        const existingItem = state.records.findIndex(
            item => item.name == record.name && item.type == record.type && item.content == record.content
        );

        state.records = state.records.filter((record, index) => index != existingItem);
    },

    /**
     * Updates all known records.
     *
     * @param {object} state
     * @param {array} ids
     */
    resetRecords(state, { records }) {
        state.records = sortRecords(records);
    },

    /**
     * Reset the dns.
     *
     * @param {object} state
     */
    reset(state) {
        state.records = [];
        state.domain = null;
        state.updating = false;
    }
};

export default {
    namespaced: true,

    state,

    getters,

    actions,

    mutations
};
